import React from 'react';
import ReactCountryFlag from 'react-country-flag';
import Localize from 'react-intl-universal';

import get from 'lodash/get';

import DateRangeIcon from '@mui/icons-material/DateRange';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PublicIcon from '@mui/icons-material/Public';
import SellIcon from '@mui/icons-material/Sell';
import Box from '@mui/material/Box';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { EVENT_STATUS_MAP } from '@common/Constants';
import { countryISOCodeByCountry } from '@common/Countries';
import { formatLongDateTimeStringToDateTimeObject, CourseNameLength } from '@common/Util';
import AudioPreview from '@components/AudioPreview/AudioPreview';

const StyledLocationOnIcon = styled(LocationOnIcon)(({ theme }) => ({
  marginRight: theme.spacing(1)
}));

const StyledCardContent = styled(CardContent)(() => ({
  flex: '1 0 auto',
  '&.MuiCardContent-root': {
    paddingBottom: '0.6rem'
  }
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  background: theme.palette.primary.main,
  color: theme.palette.common.white,
  position: 'absolute',
  top: '0.7rem',
  left: '8px'
}));

const StyledPrice = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 'bold',
  fontSize: '12px',
  [theme.breakpoints.up('md')]: {
    fontSize: '14px'
  }
}));

const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  [theme.breakpoints.up('sm')]: {
    height: '5.4rem'
  }
}));

const StyledDatePlace = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  color: theme.palette.grey[100],
  [theme.breakpoints.up('xs')]: {
    maxWidth: '95%'
  },
  [theme.breakpoints.up('md')]: {
    maxWidth: '99%'
  },
  [theme.breakpoints.up('lg')]: {
    maxWidth: '100%'
  }
}));

const ContentDescriptionName = styled(Box, { shouldForwardProp: (prop) => prop !== 'nameLength' })(
  ({ nameLength }) => ({
    paddingTop: '0.8rem',
    paddingBottom: nameLength > CourseNameLength ? '0.5rem' : '0.4rem'
  })
);

const EvtNameHeader = styled(Typography)(({ theme }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  fontWeight: '600',
  textAlign: 'left',
  lineHeight: 1.2,
  maxWidth: '90%',
  fontSize: '1rem',
  [theme.breakpoints.up('xl')]: {
    fontSize: '1.2rem'
  }
}));

const TrainingLocationText = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  paddingRight: '1rem',
  maxWidth: '180px',
  [theme.breakpoints.up('md')]: {
    maxWidth: '350px'
  }
}));

const StyledPublicIcon = styled(PublicIcon)(({ theme }) => ({
  marginRight: theme.spacing(1),
  marginTop: theme.spacing(1)
}));

const Content = ({
  config,
  eventTypeName = '',
  startDate = '',
  endDate = '',
  eventName = '',
  languages = [],
  price = '',
  city,
  country,
  maxSeats,
  location = '',
  currency = '',
  event = {}
}) => {
  return (
    <StyledCardContent>
      <Box display='flex' flexDirection='column'>
        <Box display='flex' sx={{ flexWrap: 'wrap', width: '84% ' }}>
          <StyledChip size='small' label={eventTypeName} />
          <Typography
            sx={(theme) => ({
              color: theme.palette.primary.main,
              marginRight: '4rem',
              marginBottom: '0.4rem',
              fontSize: '1rem'
            })}
          >
            {eventTypeName}
          </Typography>

          <Box display='flex' sx={{ alignContent: 'center' }}>
            {languages.map(
              (lang) =>
                lang?.value && (
                  <ReactCountryFlag
                    key={lang?.value}
                    countryCode={countryISOCodeByCountry[get(lang, 'key')?.toLowerCase()]}
                    svg
                    alt={lang.value}
                    style={{
                      width: '1.3rem',
                      height: 'auto',
                      marginRight: '1rem'
                    }}
                  />
                )
            )}
            <SellIcon sx={{ mr: 0.5 }} />
            <StyledPrice>
              {parseInt(price)
                ? `${price} ${currency} `
                : Localize.get('CustomerLoyaltyEvents.free')}
            </StyledPrice>
            {event?.status?.id === EVENT_STATUS_MAP.IN_PREVIEW && (
              <Chip
                sx={(theme) => ({
                  ml: 1,
                  backgroundColor: theme.palette.warning.light,
                  color: theme.palette.warning.contrastText
                })}
                size='small'
                label={Localize.get('Catalog.upcomingEvent')}
              />
            )}
            <Box display='flex' sx={{ position: 'absolute', right: '2rem', alignItems: 'center' }}>
              {maxSeats ? <Typography variant='body2'>{maxSeats}</Typography> : null}
              {maxSeats ? <EventSeatIcon sx={{ ml: 0.8 }} /> : null}
            </Box>
          </Box>
        </Box>
        <StyledBox>
          <ContentDescriptionName nameLength={eventName?.length}>
            <EvtNameHeader config={config}>{eventName}</EvtNameHeader>
          </ContentDescriptionName>

          <StyledDatePlace>
            <DateRangeIcon fontSize='small' sx={{ mr: 2 }} />
            <Typography sx={{ mr: 1 }}>
              {formatLongDateTimeStringToDateTimeObject(startDate)}
            </Typography>
            <Typography sx={{ mr: 3 }}>
              {formatLongDateTimeStringToDateTimeObject(endDate)}
            </Typography>
            {config?.footer?.columns?.includes('globeIcon') && (
              <Tooltip title='Global'>
                <StyledPublicIcon />
              </Tooltip>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap-reverse' }}>
              {location && (
                <Tooltip sx={{ mr: 1 }} title='Marker'>
                  <StyledLocationOnIcon />
                </Tooltip>
              )}
              <TrainingLocationText>{location}</TrainingLocationText>
              <Typography sx={{ mr: 1 }}>{city}</Typography>
              <Typography mr={3}>{country}</Typography>
            </Box>
            <AudioPreview isCatalog={true} url={event?.audioDescription?.url} />
          </StyledDatePlace>
        </StyledBox>
      </Box>
    </StyledCardContent>
  );
};

export default Content;
